import { Stack as ExpoStack, useLocalSearchParams } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect } from 'react';
import { ActivityIndicator, RefreshControl, StyleSheet, View } from 'react-native';
import { SafeAreaView, useSafeAreaInsets } from 'react-native-safe-area-context';

import { BackChevron } from '@/components/BackChevron';
import { DEAL_HERO_OVERLAP, DealDetailHero } from '@/components/DealDetailHero';
import { EmptyState } from '@/components/EmptyState';
import { RewardDealBody } from '@/components/RewardDealBody';
import { Screen } from '@/components/Screen';
import { SocialDealBody } from '@/components/social/SocialDealBody';
import { UPLOADER_ID } from '@/components/social/SocialInlineUpload';
import { SocialDealHero } from '@/components/social/SocialDealHero';
import { Stack } from '@/components/Stack';
import { colors, radii, spacing } from '@/constants/theme';
import { useDealActionPolling } from '@/hooks/useDealActionPolling';
import { useDealCta } from '@/hooks/useDealCta';
import { useHeroStatusBar } from '@/hooks/useHeroStatusBar';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import { bandShowing } from '@/stores/voltrUploadTypes';
import { useVideoScroll } from '@/hooks/useVideoScroll';
import { dealsStore, isSettled } from '@/stores/deals.store';
import { voltrStore } from '@/stores/voltr.store';

const DealDetailScreen = observer(() => {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const deal = slug ? dealsStore.bySlug(slug) : undefined;
  const action = deal ? dealsStore.actionFor(deal.id) : undefined;
  const { ctaError, handleCtaPress, readBrief, socialBusy } = useDealCta(deal, action);
  // Once the green hero has scrolled away, the status bar flips to dark text
  // over a band in the page's gray (NIL Club's pattern, minus the blur) instead of a fixed green strip.
  const { pastHero, focused, onHeroLayout, onScroll } = useHeroStatusBar(DEAL_HERO_OVERLAP);
  const videoScroll = useVideoScroll();
  const insets = useSafeAreaInsets();

  // Cold deep links reach this screen before any tab loaded the store
  // (ensureLoaded, or "Deal not found" for a real deal); refreshDeal keeps
  // spots_left honest: the cached deal renders, the fresh one swaps in.
  useEffect(() => {
    void dealsStore.ensureLoaded();
    if (slug) void dealsStore.refreshDeal(slug);
  }, [slug]);

  const onRefresh = useCallback(() => {
    const current = slug ? dealsStore.bySlug(slug) : undefined;
    if (slug) void dealsStore.pullRefreshDeal(slug, current?.id);
    // A social deal's products and brief come from /social, not the deal row.
    if (current?.social) void voltrStore.load(current.slug);
  }, [slug]);

  const settled = isSettled(action);
  useDealActionPolling(deal?.id, settled);

  if (!deal && dealsStore.loading) {
    return (
      <>
        <ExpoStack.Screen options={{ headerShown: false }} />
        <Screen>
          <View style={styles.loading}>
            <ActivityIndicator color={colors.primary} />
          </View>
        </Screen>
      </>
    );
  }

  if (!deal) {
    return (
      <>
        <ExpoStack.Screen options={{ headerShown: false }} />
        <Screen>
          <Stack gap="lg">
            <BackChevron />
            <EmptyState title="Deal not found" body="This deal may have ended or isn't visible to you." />
          </Stack>
        </Screen>
      </>
    );
  }

  const ctaLoading = dealsStore.clicking || dealsStore.advancing || socialBusy;

  return (
    <>
      <ExpoStack.Screen options={{ headerShown: false }} />
      {/* Light over the green hero, dark over the gray band or the upload band; the
          root layout's style returns on unmount or while a screen is pushed over this one. */}
      {focused ? <StatusBar style={pastHero || bandShowing(voltrUploadStore.running, voltrUploadStore.items) ? 'dark' : 'light'} /> : null}
      <Screen
        padding="none"
        edges={NO_EDGES}
        background="deepGreen"
        onScroll={onScroll}
        scrollRef={videoScroll.scrollRef}
        contentContainerStyle={styles.scrollContent}
        refreshControl={
          <RefreshControl
            refreshing={dealsStore.pullRefreshingDeal}
            onRefresh={onRefresh}
            tintColor={colors.frost}
            // The screen takes no safe-area edge (the hero runs under the
            // status bar), so without this the spinner draws behind the notch.
            progressViewOffset={insets.top}
          />
        }
      >
        {/* Full-bleed header, then a sheet riding up over its bottom edge. A
            Social deal (`deal.social`) draws its own hero and sections. */}
        {deal.social ? (
          <SocialDealHero
            deal={deal}
            action={action}
            // "Upload content" scrolls to the upload step's box.
            onCtaPress={(step, cta) => (cta?.action === 'voltr_upload' ? videoScroll.scrollToVideo(UPLOADER_ID) : void handleCtaPress(step, cta))}
            ctaLoading={ctaLoading}
            onLayout={onHeroLayout}
            onFixVideo={videoScroll.scrollToVideo}
          />
        ) : (
          <DealDetailHero deal={deal} onLayout={onHeroLayout} testID="deal-hero" />
        )}
        {/* The bottom inset keeps the fine print clear of the home indicator
            (the screen itself takes no safe-area edges). */}
        <SafeAreaView edges={['bottom']} style={[styles.sheet, deal.social && styles.socialSurface, deal.social && styles.socialSheet]}>
          {deal.social ? (
            <SocialDealBody
              deal={deal}
              action={action}
              onCtaPress={handleCtaPress}
              onReadBrief={(step) => void readBrief(step)}
              ctaLoading={ctaLoading}
              ctaError={ctaError}
              videoScroll={videoScroll}
            />
          ) : (
            <RewardDealBody
              deal={deal}
              action={action}
              onCtaPress={handleCtaPress}
              ctaLoading={ctaLoading}
              ctaError={ctaError}
              settled={settled}
            />
          )}
        </SafeAreaView>
      </Screen>
      {pastHero ? (
        <SafeAreaView
          edges={['top']}
          style={[styles.statusScrim, deal.social && styles.socialSurface]}
          pointerEvents="none"
          testID="deal-status-scrim"
        />
      ) : null}
    </>
  );
});

export default DealDetailScreen;

/** The hero runs up under the status bar and takes the inset itself. */
const NO_EDGES = [] as const;

const styles = StyleSheet.create({
  // Lets the sheet fill a short page, so no green shows below it.
  scrollContent: {
    flexGrow: 1,
  },
  // The content sheet: rounded top corners over the hero's bottom edge.
  sheet: {
    flexGrow: 1,
    marginTop: -DEAL_HERO_OVERLAP,
    borderTopLeftRadius: radii['2xl'],
    borderTopRightRadius: radii['2xl'],
    backgroundColor: colors.surfaceSunken,
    padding: spacing.base,
    gap: spacing.base,
  },
  // Figma "Apply" frame: near-white page (shared by the status scrim over
  // it), 24pt side margins, wider breaks between sections.
  socialSurface: { backgroundColor: colors.background },
  socialSheet: { paddingHorizontal: spacing.lg, gap: spacing.lg },
  statusScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    // Solid, not translucent: without a native blur (not OTA-able), see-through
    // content under the clock reads as a stray bar.
    backgroundColor: colors.surfaceSunken,
  },
  loading: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: spacing['2xl'],
  },
});
